export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const SERVICES = {
  individual: {
    key: "individual",
    num: "01",
    name: "Private/Semi-Private Tests",
    label: "Book a private/semi-private test",
    price: 70,
    unit: "per student (1–3)",
    desc: "$70 for each student for the first 3 students ($50 each for 4 or more students). In person, with a state-approved, nationally normed achievement test.",
    formTitle: "Book a private/semi-private test",
    formSub: "One to three students, tested together in person.",
    minStudents: 1,
    maxStudents: 3,
  },
  group: {
    key: "group",
    num: "02",
    name: "Group Tests",
    label: "Book a group test",
    price: 50,
    unit: "each (4 or more students)",
    desc: "For 4 or more students — co-ops and multi-student families. $50 each. Add each student's name when you book.",
    formTitle: "Book a group test",
    formSub: "For co-ops and families testing 4 or more students together.",
    minStudents: 4,
    maxStudents: 30,
  },
  consultation: {
    key: "consultation",
    num: "03",
    name: "Consultation",
    label: "Book a 1-hr consultation",
    price: 65,
    unit: "per hour",
    desc: "Understand your child's results, plan next steps, or talk through curriculum.",
    formTitle: "Book a 1-hour consultation",
    formSub: "Talk through test results, next steps, or curriculum planning.",
  },
  tutoring: {
    key: "tutoring",
    num: "04",
    name: "Tutoring",
    label: "Book a tutoring session",
    price: 45,
    unit: "/ student / hr",
    desc: "Reading, writing, spelling and math support matched to your curriculum.",
    formTitle: "Book a tutoring session",
    formSub: "Reading, writing, spelling and math support.",
  },
};

export const SERVICE_ORDER = ["individual", "group", "consultation", "tutoring"];

export const COUNTIES = ["Clackamas", "Clatsop", "Columbia", "Marion", "Multnomah", "Washington", "Yamhill"];

export const WASHINGTON_AREAS = ["Clark County / Vancouver", "Camas", "Battle Ground"];

export const FAQ_GROUPS = [
  {
    category: "State testing requirements",
    items: [
      {
        q: "Does my homeschooled child need to be tested?",
        a: "Oregon: Yes. Testing is required in grades 3, 5, 8 and 10, must be given by a qualified tester, and must be done by August 15. Your child's grade is set by age. Washington: Yes, every year. Children ages 8–18 need an annual assessment, and a standardized test like ours meets that requirement.",
      },
      {
        q: "When should we book?",
        a: "You can book any time of year. Spring, March through August, is the busiest season, so book early for the best choice of dates. Oregon families also leave time before the August 15 deadline.",
      },
      {
        q: "What if we miss Oregon's August 15 deadline?",
        a: "Missing required testing breaks Oregon homeschool law and can bring a Class C misdemeanor and a fine. Contact your local ESD, then book a test as soon as you can. Julie can usually find a date.",
      },
      {
        q: "Can my child test in a year that isn't required?",
        a: "Yes. Julie tests grades 1–12, and many families test every year to track progress. Oregon students who want to join school sports, band, drama or other interscholastic activities must test every year by August 15 and score above the 25th percentile.",
      },
    ],
  },
  {
    category: "About the test",
    items: [
      {
        q: "Which test do you use?",
        a: "Julie uses the TerraNova Third Edition Survey for grades 1–12. It's a nationally normed achievement test, approved in Oregon and accepted in Washington.",
      },
      {
        q: "Is it a Common Core test?",
        a: "No. TerraNova measures general grade-level skills rather than a particular curriculum, so it works well whatever your homeschool approach.",
      },
      {
        q: "What does the test cover?",
        a: "It's multiple choice, in three main sections: Reading, Language Arts and Math.",
      },
      {
        q: "Can the test be taken online?",
        a: "No. All testing is in person, at your home or your co-op. Children tend to feel calmer and focus better with Julie there to guide them.",
      },
    ],
  },
  {
    category: "What to expect on test day",
    items: [
      {
        q: "Where does testing take place?",
        a: "Individual students: Julie comes to your home, so your child tests somewhere familiar and relaxed. Co-ops and groups: Julie tests on-site at your co-op. Your co-op provides the address and a quiet room. Julie serves Clackamas, Clatsop, Columbia, Marion, Multnomah, Washington and Yamhill counties in Oregon, and Clark County in Washington. She can travel farther for a fee.",
      },
      {
        q: "How long does testing take?",
        a: "There is a break in the middle.",
        bullets: ["Grades 1–3: plan for 2 hours", "Grades 4–12: allow 2½ hours"],
      },
      {
        q: "What should my child bring?",
        bullets: ["Pencils", "A calculator (optional, grades 3–12)", "A snack and a drink for the break"],
      },
      {
        q: "What materials do you provide?",
        a: "Julie brings the test booklet and a small ruler for the math section. Students in grades 1–3 write their answers in the booklet; grades 4–12 use a separate answer sheet. Calculators are allowed but not required for grades 3–12.",
      },
      {
        q: "My child gets anxious or has an IEP. Can you help?",
        a: "Yes. Julie has been testing children since 1996, and her patient, unhurried approach helps nervous children settle in. Testing at home helps too. In Oregon, accommodations in an IEP or PDP (a homeschool learning plan) are honored, and some children may be exempt from testing; tell your ESD if so.",
      },
      {
        q: "How can my child prepare?",
        a: "Your child doesn't need to cram. A good night's sleep and a relaxed morning matter most. Practice questions are on the test publisher's website, and Julie is glad to suggest what to focus on.",
      },
    ],
  },
  {
    category: "Pricing and booking",
    items: [
      {
        q: "How much does testing cost?",
        bullets: ["Individual testing (at your home): $70 per student", "Groups of 4 or more: $50 per student"],
        a: "You pay when you book, and that holds your time slot. Travel outside the service area may add a fee.",
      },
      {
        q: "How do I book?",
        a: "Choose a date and pay online through our booking page. For co-op or group testing, contact Julie with your co-op's address, likely number of students and preferred dates.",
      },
    ],
  },
  {
    category: "After testing",
    items: [
      {
        q: "How do we get our results?",
        a: "Julie scores the test and normally emails your results, with an explanation of what the scores mean. If you'd prefer, she can send them by regular mail.",
      },
      {
        q: "What do we do with the results?",
        a: "Oregon: Keep them, and send them to your ESD only if it asks. Washington: Keep them in your own records; they aren't submitted unless your child later enrolls in public school.",
      },
      {
        q: "What if my child scores low?",
        a: "Oregon: If your child scores at or above the 15th percentile, no further action is needed. Below that, your ESD will ask you to test again the following year. Washington: The law asks parents to make a good-faith effort to address any gaps. In either state, Julie can help you plan next steps.",
      },
    ],
  },
  {
    category: "Groups and co-ops",
    items: [
      {
        q: "Do you test homeschool co-ops and groups?",
        a: "Yes, in both states. Julie comes to your co-op's location, so families don't need to travel, and groups of 4 or more students pay $50 each instead of $70.",
      },
      {
        q: "Can our co-op use you as its tester?",
        a: "Yes. Julie is an ODE-approved tester, uses the state-approved TerraNova test, and is never related to the students she tests, which is what Oregon requires. She tests Washington co-ops too.",
      },
    ],
  },
];

export const PHONE = "503-449-8323";
export const EMAIL = "readyandright@gmail.com";
